import { usePage } from '@inertiajs/react';
import { Box, Stack } from '@mantine/core';
import type { ReactNode } from 'react';

import FlashMessages from '@/components/layout/FlashMessages';
import GlobalSpotlight from '@/components/layout/GlobalSpotlight';
import NotificationBanner from '@/components/layout/NotificationBanner';
import TopNavbar from '@/components/layout/TopNavbar';
import OrganizationsSidebar from '@/components/pro/OrganizationsSidebar';
import useCrispChat from '@/hooks/useCrispChat';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { SharedProps } from '@/types';

export default function DefaultLayout({ children, fullScreen = false }: { children: ReactNode; fullScreen?: boolean }) {
  const { current_organization: currentOrganization, current_account: currentAccount } = usePage<SharedProps>().props;
  const { isMobile } = useMediaQuery();
  const padding = fullScreen ? 0 : 'md';
  // Switching organization stays in the settings section you are reading, instead of always
  // sending you back to the authorizations.
  const settingsSection = window.location.pathname.split('/')[3] ?? 'authorizations';

  useCrispChat();

  return (
    <Stack h='100dvh' gap={0}>
      <FlashMessages />
      {/* This layout also renders signed out (sign in page), where there is nothing to search. */}
      {currentAccount && <GlobalSpotlight />}
      <NotificationBanner />
      <TopNavbar />
      <Box flex={1} display='flex' style={{ overflow: 'hidden', flexDirection: isMobile ? 'column' : 'row' }}>
        {/* The settings pages carry their own sections in the navigation menu, and only need the switcher. */}
        {currentOrganization && (
          <OrganizationsSidebar
            getOrganizationHref={(organizationId) => `/organizations/${organizationId}/${settingsSection}`}
            selectedOrganizationId={currentOrganization.id}
            disableExternalOrganizations
          />
        )}

        <Stack component='main' flex={1} p={padding} pb={fullScreen ? 0 : 'xl'} gap='md' style={{ overflow: 'auto' }}>
          {children}
        </Stack>
      </Box>
    </Stack>
  );
}
